import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Bell, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { insightsApi } from '../../api/insights';

// Sound effect for high-severity alerts
const ALERT_SOUND_URL = '/sounds/insight-alert.mp3'; // Optional: add sound file to public folder

export default function RealtimeInsightAlert() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('insight_sound_enabled') !== 'false';
  });
  const [audio] = useState(() => {
    // Create audio element only if sound is desired
    const a = new Audio(ALERT_SOUND_URL);
    a.volume = 0.5;
    return a;
  });

  // Check for new high-severity insights
  const checkForNewAlerts = useCallback(async () => {
    try {
      // Get unnotified high severity insights
      const newAlerts = await insightsApi.getUnnotifiedHighSeverity();

      if (newAlerts.length > 0) {
        // Play sound if enabled
        if (soundEnabled) {
          audio.play().catch(() => {
            // Audio play failed (likely due to browser autoplay policy)
          });
        }

        // Add new alerts to state, avoiding duplicates
        setAlerts(prev => {
          const existingIds = new Set(prev.map(a => a.id));
          const uniqueNew = newAlerts.filter(a => !existingIds.has(a.id));
          return [...uniqueNew, ...prev].slice(0, 5); // Keep max 5 alerts
        });
      }
    } catch (error) {
      console.error('Failed to check for new alerts:', error);
    }
  }, [soundEnabled, audio]);

  // Poll for new alerts every 30 seconds
  useEffect(() => {
    // Initial check
    checkForNewAlerts();

    // Set up polling
    const interval = setInterval(checkForNewAlerts, 30000);

    return () => clearInterval(interval);
  }, [checkForNewAlerts]);

  // Also check when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      checkForNewAlerts();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkForNewAlerts]);

  const dismissAlert = (alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const dismissAll = () => {
    setAlerts([]);
  };

  const handleAlertClick = (alert) => {
    dismissAlert(alert.id);
    navigate('/insights');
  };

  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    localStorage.setItem('insight_sound_enabled', newState.toString());
  };

  if (alerts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 w-full max-w-sm space-y-3">
      {/* Header controls */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-red-600 dark:text-red-400 flex items-center gap-1.5">
          <AlertTriangle size={14} />
          {t('insights.alerts.highSeverity', { count: alerts.length })}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleSound}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title={soundEnabled ? t('insights.alerts.mute') : t('insights.alerts.unmute')}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button
            onClick={dismissAll}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title={t('insights.alerts.dismissAll')}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Alert Cards */}
      {alerts.map((alert, index) => (
        <div
          key={alert.id}
          onClick={() => handleAlertClick(alert)}
          className="bg-white dark:bg-gray-900 border border-red-200 dark:border-red-800 rounded-xl shadow-lg p-4 cursor-pointer hover:shadow-xl transition-all animate-in slide-in-from-right duration-300"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
              <Bell size={20} className="text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                {alert.message}
              </p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {t('insights.alerts.clickToView')}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissAlert(alert.id);
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
